import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  TextField,
  LinearProgress,
  Box,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import StarIcon from "@mui/icons-material/Star";
import SearchIcon from "@mui/icons-material/Search";
import { getFaqs, rateFaq } from "../../actions/faq";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";

function FaqUserComponent() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [votedQuestions, setVotedQuestions] = useState(new Set());
  const [t, i18next] = useTranslation("global");
  const language = i18next.language; 

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await getFaqs();
        if (response.status === 200) {
          setFaqs(response.data);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching FAQs:", error);
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  const filteredFaqs = React.useMemo(() => {
    if (!searchTerm) return faqs;

    const searchLower = searchTerm.toLowerCase();
    return faqs
      .map((category) => ({
        ...category,
        faqs: category.faqs.filter(
          (faq) =>
            faq.question_es.toLowerCase().includes(searchLower) ||
            faq.question_en.toLowerCase().includes(searchLower) ||
            faq.answer_es.toLowerCase().includes(searchLower) ||
            faq.answer_en.toLowerCase().includes(searchLower)
        ),
      }))
      .filter((category) => category.faqs.length > 0); 
  }, [faqs, searchTerm]);

  const sortedFaqs = React.useMemo(() => {
    return filteredFaqs.map((category) => ({
      ...category,
      faqs: [...category.faqs].sort((a, b) => {
        if (a.is_featured && !b.is_featured) return -1;
        if (!a.is_featured && b.is_featured) return 1;
        return b.useful_yes - a.useful_yes;
      }),
    }));
  }, [filteredFaqs]);

  const handleVote = async (faqId, isUseful) => {
    if (votedQuestions.has(faqId)) return;

    try {
      const response = await rateFaq(faqId, isUseful);
      if (response.status === 200) {
        setVotedQuestions((prev) => new Set(prev.add(faqId)));

        setFaqs((prev) =>
          prev.map((category) => ({
            ...category,
            faqs: category.faqs.map((faq) =>
              faq.id === faqId
                ? {
                    ...faq,
                    useful_yes: isUseful ? faq.useful_yes + 1 : faq.useful_yes,
                    useful_no: !isUseful ? faq.useful_no + 1 : faq.useful_no,
                  }
                : faq
            ),
          }))
        );
      }
    } catch (error) {
      console.error("Error voting:", error);
    }
  };

  return (
    <Box sx={{ width: "1000px", margin: "auto", marginBottom: "150px" }}>
      <TextField
        fullWidth
        placeholder={t("FAQ.search_placeholder")}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ marginBottom: 3 }}
        InputProps={{
          startAdornment: <SearchIcon sx={{ marginRight: 1 }} />, 
        }}
      />

      {loading && <LinearProgress color="secondary" />}

      {sortedFaqs.map((category) => (
        <Accordion key={category.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#FF6A13" }} />}>
            <Typography sx={{ fontWeight: "bold" }}>
              {language === "en" ? category.name_en : category.name_es}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {category.faqs.map((faq) => (
                <Accordion
                  key={faq.id}
                  expanded={expandedQuestion === faq.id}
                  onChange={() => setExpandedQuestion(expandedQuestion === faq.id ? null : faq.id)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {faq.is_featured && <StarIcon sx={{ color: "gold", marginRight: 1 }} />}
                      <Typography>
                        {language === "en" ? faq.question_en : faq.question_es}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          language === "en" ? faq.answer_en : faq.answer_es
                        ),
                      }}
                    />

                    <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                      <Typography variant="body2" sx={{ marginRight: 2 }}>
                        {t("FAQ.was_this_helpful")}
                      </Typography>
                      <IconButton
                        onClick={() => handleVote(faq.id, true)}
                        disabled={votedQuestions.has(faq.id)}
                        color={votedQuestions.has(faq.id) ? "primary" : "default"}
                      >
                        <ThumbUpIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleVote(faq.id, false)}
                        disabled={votedQuestions.has(faq.id)}
                        color={votedQuestions.has(faq.id) ? "error" : "default"}
                      >
                        <ThumbDownIcon />
                      </IconButton>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

export default FaqUserComponent;